import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Building2, Globe, Phone, MapPin, TrendingUp, Users, GitPullRequest,
    ChevronLeft, Edit3, Plus, Mail, Activity, Network, FileText,
    ArrowUpRight, Sparkles, MoreHorizontal, ExternalLink, Zap, ChevronRight,
    Loader2, Linkedin, ChevronDown, Tag, Trash2, MapPinned, Search, Briefcase,
    UserCheck, Clock, Eye, Workflow, History, X, UserPlus, Database, GraduationCap
} from 'lucide-react';
import EnrollCampaignModal from '../components/EnrollCampaignModal';
import OrgChart from '../components/OrgChart';
import DISCBadge from '../components/DISCBadge';
import MdFilePanel from '../components/MdFilePanel';
import PitchBookRecordTab from '../components/pitchbook/PitchBookRecordTab';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { usePlan } from '../context/PlanContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = { 'Prospect': 'bg-blue-50 text-blue-700', 'Qualified': 'bg-stone-100 text-stone-900', 'Proposal Sent': 'bg-amber-50 text-amber-700', 'Negotiation': 'bg-violet-50 text-violet-700', 'Closed Won': 'bg-emerald-50 text-emerald-700', 'Closed Lost': 'bg-red-50 text-red-700' };

const formatDate = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    if (isNaN(d)) return ts;
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const ROLE_COLORS = { Champion: 'bg-emerald-100 text-emerald-700', 'Decision Maker': 'bg-stone-100 text-stone-900', Influencer: 'bg-blue-100 text-blue-700', Blocker: 'bg-red-100 text-red-700', User: 'bg-stone-100 text-stone-700' };

function insightHasValue(value) {
    return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
}

function formatInsightValue(value, { money = false, date = false } = {}) {
    if (!insightHasValue(value)) return '—';
    if (date) {
        const d = new Date(value);
        return isNaN(d) ? String(value) : d.toLocaleDateString();
    }
    if (money && typeof value === 'number') {
        if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
        if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
        if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
        return `$${value.toLocaleString()}`;
    }
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function InsightSection({ title, description, fields }) {
    const visible = fields.filter(f => insightHasValue(f.value));
    if (!visible.length) return null;
    return (
        <section className="overflow-hidden bg-white rounded-xl border border-stone-200">
            <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/60">
                <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
                {description && <p className="text-xs text-stone-500 mt-0.5">{description}</p>}
            </div>
            <div className="grid grid-cols-1 gap-y-3 gap-x-8 p-4 md:grid-cols-2">
                {visible.map(f => (
                    <div key={f.label} className="flex gap-3">
                        <span className="w-40 text-xs font-semibold tracking-wider uppercase text-stone-400 shrink-0">{f.label}</span>
                        {f.isLink ? (
                            <a href={String(f.value).startsWith('http') ? f.value : `https://${f.value}`} target="_blank" rel="noreferrer"
                                className="text-sm text-blue-600 break-all hover:underline">
                                {f.value}
                            </a>
                        ) : (
                            <span className="text-sm break-words text-stone-700">{formatInsightValue(f.value, f)}</span>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}

function AccountDataInsights({ account }) {
    const money = true;
    return (
        <div className="space-y-4">
            <div className="p-4 rounded-xl border bg-stone-50 border-stone-200">
                <div className="flex gap-3 items-start">
                    <Database className="w-5 h-5 text-stone-500 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Data Insights</h3>
                        <p className="mt-1 text-sm text-stone-600">Additional structured intelligence imported or enriched from trusted data sources. Core CRM fields stay on Overview.</p>
                    </div>
                </div>
            </div>
            <InsightSection title="Identity & Classification" fields={[
                { label: 'Legal Name', value: account.legal_name },
                { label: 'Also Known As', value: account.also_known_as },
                { label: 'Former Name', value: account.former_name },
                { label: 'Short Description', value: account.description_short },
                { label: 'Keywords', value: account.keywords },
                { label: 'Ticker', value: account.ticker },
                { label: 'Exchange', value: account.exchange },
            ]} />
            <InsightSection title="Market Intelligence" fields={[
                { label: 'Industry Group', value: account.industry_group },
                { label: 'Industry Code', value: account.industry_code },
                { label: 'All Industries', value: account.all_industries },
                { label: 'Verticals', value: account.industry_vertical },
                { label: 'Emerging Spaces', value: account.emerging_spaces },
                { label: 'Universe', value: account.universe },
                { label: 'Ownership Status', value: account.ownership_status },
                { label: 'Ownership Date', value: account.ownership_status_date, date: true },
                { label: 'Financing Status', value: account.financing_status },
                { label: 'Financing Date', value: account.financing_status_date, date: true },
                { label: 'Business Status', value: account.business_status },
                { label: 'Business Date', value: account.business_status_date, date: true },
            ]} />
            <InsightSection title="Corporate Structure & Location" fields={[
                { label: 'Parent Company', value: account.parent_company },
                { label: 'Top-Level Parent', value: account.top_level_parent },
                { label: 'HQ Location', value: account.hq_location },
                { label: 'Address Line 2', value: account.address_line2 },
                { label: 'Postcode', value: account.postcode },
                { label: 'Sub-Region', value: account.sub_region },
                { label: 'Fax', value: account.fax },
                { label: 'Employee Count Date', value: account.employee_count_date, date: true },
            ]} />
            <InsightSection title="Financials" fields={[
                { label: 'Total Raised', value: account.total_raised, money },
                { label: 'Native Raised', value: account.total_raised_native },
                { label: 'Native Currency', value: account.total_raised_currency },
                { label: 'Enterprise Value', value: account.enterprise_value, money },
                { label: 'Gross Profit', value: account.gross_profit, money },
                { label: 'Net Income', value: account.net_income, money },
                { label: 'EBITDA', value: account.ebitda, money },
                { label: 'EBIT', value: account.ebit, money },
                { label: 'Net Debt', value: account.net_debt, money },
                { label: 'Fiscal Period', value: account.fiscal_period },
                { label: 'Period End', value: account.fiscal_period_end, date: true },
            ]} />
            <InsightSection title="Investor Metrics" fields={[
                { label: 'AUM', value: account.aum, money },
                { label: 'Dry Powder', value: account.dry_powder, money },
                { label: 'Total Investments', value: account.total_investments },
                { label: 'Total Exits', value: account.total_exits },
                { label: 'Active Portfolio', value: account.total_active_portfolio },
                { label: 'Other Investor Types', value: account.other_investor_types },
                { label: 'Trade Associations', value: account.trade_associations },
                { label: 'Investment Pros', value: account.investment_professional_count },
                { label: 'Median Round', value: account.median_round_amount, money },
                { label: 'Median Valuation', value: account.median_valuation, money },
                { label: 'Min Fund Size', value: account.min_fund_size, money },
                { label: 'Max Fund Size', value: account.max_fund_size, money },
                { label: 'Open Funds', value: account.total_funds_open },
                { label: 'Closed Funds', value: account.total_funds_closed },
            ]} />
            <InsightSection title="Source Metadata" fields={[
                { label: 'Source', value: account.source },
                { label: 'Created By', value: account.created_by },
                { label: 'Created At', value: account.created_at, date: true },
                { label: 'Updated At', value: account.updated_at, date: true },
            ]} />
        </div>
    );
}

function OrgChartSimple({ nodes }) {
    if (!nodes.length) return <div className="py-8 text-center text-stone-400">No org chart data</div>;
    const rootNodes = nodes.filter(n => !n.parent_node_id);
    const getChildren = (nodeId) => nodes.filter(n => n.parent_node_id === nodeId);

    const NodeCard = ({ node, depth = 0 }) => {
        const children = getChildren(node.node_id);
        return (
            <div className="flex flex-col items-center">
                <div className={`w-44 bg-white border-2 ${depth === 0 ? 'border-stone-300' : 'border-stone-200'} rounded-xl p-3 shadow-sm hover:shadow-md hover:border-stone-400 transition-all text-center`}>
                    <div className="overflow-hidden mx-auto mb-2 w-10 h-10 rounded-full bg-stone-100">
                        {node.avatar_url ? <img src={node.avatar_url} alt={node.name} className="object-cover w-full h-full" /> : <div className="flex justify-center items-center w-full h-full text-sm font-bold text-stone-700">{node.name?.charAt(0)}</div>}
                    </div>
                    <p className="text-xs font-semibold truncate text-stone-900">{node.name}</p>
                    <p className="text-xs truncate text-stone-400">{node.title}</p>
                    {node.role_in_deal && <span className={`text-xs px-1.5 py-0.5 rounded-full mt-1 inline-block ${ROLE_COLORS[node.role_in_deal] || 'bg-stone-100 text-stone-600'}`}>{node.role_in_deal}</span>}
                </div>
                {children.length > 0 && (
                    <div className="flex flex-col items-center">
                        <div className="w-0.5 h-6 bg-stone-200" />
                        <div className="flex gap-4">
                            {children.map(child => (
                                <div key={child.node_id} className="flex flex-col items-center">
                                    <div className="w-0.5 h-4 bg-stone-200" />
                                    <NodeCard node={child} depth={depth + 1} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="overflow-auto p-4">
            <div className="flex gap-8 justify-center">
                {rootNodes.map(node => <NodeCard key={node.node_id} node={node} />)}
            </div>
        </div>
    );
}

function LinkedInCompanySection({ data }) {
    const [expanded, setExpanded] = useState(true);
    if (!data) return null;

    const specialties = data.specialties || data.specialities || [];
    const description = data.description || data.about || '';
    const employeeCount = data.employee_count || data.staff_count || data.company_size;
    const employeeRange = data.employee_count_range || data.staff_count_range || '';
    const updates = data.updates || data.recent_updates || [];

    return (
        <div className="mt-6 bg-white rounded-xl border border-stone-200" data-testid="linkedin-company-section">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex justify-between items-center px-5 py-3 w-full border-b border-stone-100"
            >
                <div className="flex gap-2 items-center">
                    <Linkedin className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>LinkedIn Company</h3>
                </div>
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
            {expanded && (
                <div className="p-5 space-y-4">
                    {/* Description */}
                    {description && (
                        <div>
                            <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-400">Description</h4>
                            <p className="text-sm leading-relaxed text-stone-600">{description}</p>
                        </div>
                    )}

                    {/* Employee Count */}
                    {(employeeCount || employeeRange) && (
                        <div>
                            <h4 className="mb-1 text-xs font-semibold tracking-wider uppercase text-stone-400">Employees</h4>
                            <p className="text-sm font-medium text-stone-700">
                                {employeeCount ? Number(employeeCount).toLocaleString() : ''}{employeeRange ? ` (${employeeRange})` : ''}
                            </p>
                        </div>
                    )}

                    {/* Specialties */}
                    {specialties.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Tag className="w-3.5 h-3.5" /> Specialties
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                                {specialties.map((spec, i) => (
                                    <span key={i} className="px-2 py-1 text-xs text-blue-700 bg-blue-50 rounded-full border border-blue-100">
                                        {typeof spec === 'string' ? spec : spec.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Recent Updates */}
                    {updates.length > 0 && (
                        <div>
                            <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-400">Recent Updates</h4>
                            <div className="space-y-2">
                                {updates.slice(0, 5).map((update, i) => (
                                    <div key={i} className="p-2.5 bg-stone-50 rounded-lg border border-stone-100">
                                        <p className="text-xs text-stone-600">{update.text || update.content || JSON.stringify(update)}</p>
                                        {update.date && <p className="mt-1 text-xs text-stone-400">{update.date}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * AccountResearchSection — surfaces the prep-brief context fields
 * (owned_products, strategic_priorities, recent_news) the Meeting Copilot
 * Prep screen reads. Admins can inline-edit each list.
 */
function AccountResearchSection({ account, onUpdate, getAuthHeader }) {
    const [editing, setEditing] = useState(null); // 'products'|'priorities'|'news'|null
    const [saving, setSaving] = useState(false);

    const [products, setProducts] = useState(account.owned_products || []);
    const [priorities, setPriorities] = useState(account.strategic_priorities || []);
    const [news, setNews] = useState(account.recent_news || []);

    useEffect(() => {
        setProducts(account.owned_products || []);
        setPriorities(account.strategic_priorities || []);
        setNews(account.recent_news || []);
    }, [account]);

    const save = async (patch) => {
        setSaving(true);
        try {
            await axios.put(`${API}/accounts/${account.account_id}`, patch, {
                headers: getAuthHeader(),
            });
            toast.success('Saved');
            setEditing(null);
            onUpdate && onUpdate();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pt-6 mt-6 border-t border-stone-200">
            <div className="flex gap-2 items-center mb-4">
                <Sparkles className="w-4 h-4 text-violet-500" />
                <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Prep-Brief Research
                </h3>
                <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400">
                    Surfaced on Meeting Copilot Prep
                </span>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* Owned Products */}
                <div className="p-4 bg-white rounded-lg border border-stone-200">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-bold tracking-widest uppercase text-stone-600">
                            Owned Products
                        </h4>
                        {editing === 'products' ? (
                            <div className="flex gap-1">
                                <button onClick={() => save({ owned_products: products })} disabled={saving}
                                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">
                                    Save
                                </button>
                                <button onClick={() => { setProducts(account.owned_products || []); setEditing(null); }}
                                    className="text-xs font-semibold text-stone-500">Cancel</button>
                            </div>
                        ) : (
                            <button onClick={() => setEditing('products')} className="text-xs font-semibold text-violet-600 hover:text-violet-700">
                                <Edit3 className="inline w-3 h-3" /> Edit
                            </button>
                        )}
                    </div>
                    {editing === 'products' ? (
                        <ProductsEditor products={products} setProducts={setProducts} />
                    ) : products.length === 0 ? (
                        <p className="text-xs italic text-stone-400">No products yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {products.map((p, i) => (
                                <li key={i} className="flex gap-2 items-center text-sm">
                                    <span className="flex-1 font-semibold">{p.product_name}</span>
                                    <span className="text-xs text-stone-500">{p.seats || 0} seats</span>
                                    <span
                                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                                        style={{
                                            background: p.health === 'healthy' ? '#E8F4EF' : p.health === 'at-risk' ? '#FCECD2' : '#FDECEC',
                                            color: p.health === 'healthy' ? '#1DA063' : p.health === 'at-risk' ? '#8A5A0C' : '#E5484D',
                                        }}
                                    >{p.health}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Strategic Priorities */}
                <div className="p-4 bg-white rounded-lg border border-stone-200">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-bold tracking-widest uppercase text-stone-600">
                            Strategic Priorities
                        </h4>
                        {editing === 'priorities' ? (
                            <div className="flex gap-1">
                                <button onClick={() => save({ strategic_priorities: priorities.filter(Boolean) })} disabled={saving}
                                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">Save</button>
                                <button onClick={() => { setPriorities(account.strategic_priorities || []); setEditing(null); }}
                                    className="text-xs font-semibold text-stone-500">Cancel</button>
                            </div>
                        ) : (
                            <button onClick={() => setEditing('priorities')} className="text-xs font-semibold text-violet-600 hover:text-violet-700">
                                <Edit3 className="inline w-3 h-3" /> Edit
                            </button>
                        )}
                    </div>
                    {editing === 'priorities' ? (
                        <textarea
                            value={priorities.join('\n')}
                            onChange={e => setPriorities(e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                            rows={5}
                            placeholder={'One per line — e.g.\nRoll out AI-powered candidate screening\nConsolidate CRM + recruiting tooling'}
                            className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md"
                        />
                    ) : priorities.length === 0 ? (
                        <p className="text-xs italic text-stone-400">No priorities yet.</p>
                    ) : (
                        <ul className="space-y-1">
                            {priorities.map((p, i) => (
                                <li key={i} className="flex gap-2 text-sm text-stone-700">
                                    <span className="flex-shrink-0 text-violet-500">·</span>{p}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Recent News */}
                <div className="p-4 bg-white rounded-lg border border-stone-200">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-bold tracking-widest uppercase text-stone-600">
                            Recent News
                        </h4>
                        {editing === 'news' ? (
                            <div className="flex gap-1">
                                <button onClick={() => save({ recent_news: news })} disabled={saving}
                                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">Save</button>
                                <button onClick={() => { setNews(account.recent_news || []); setEditing(null); }}
                                    className="text-xs font-semibold text-stone-500">Cancel</button>
                            </div>
                        ) : (
                            <button onClick={() => setEditing('news')} className="text-xs font-semibold text-violet-600 hover:text-violet-700">
                                <Edit3 className="inline w-3 h-3" /> Edit
                            </button>
                        )}
                    </div>
                    {editing === 'news' ? (
                        <NewsEditor news={news} setNews={setNews} />
                    ) : news.length === 0 ? (
                        <p className="text-xs italic text-stone-400">No news yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {news.map((n, i) => (
                                <li key={i}>
                                    {n.published && <span className="text-[10px] text-stone-400 uppercase tracking-widest font-bold">{n.published}</span>}
                                    <div className="text-sm text-stone-700">{n.headline}</div>
                                    {n.url && (
                                        <a href={n.url} target="_blank" rel="noopener noreferrer"
                                            className="text-[10px] text-violet-600 hover:underline">
                                            {n.source || 'Source'}
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function ProductsEditor({ products, setProducts }) {
    const add = () => setProducts([...products, { product_name: '', seats: 0, health: 'healthy' }]);
    const update = (i, patch) => {
        const next = [...products];
        next[i] = { ...next[i], ...patch };
        setProducts(next);
    };
    const remove = (i) => setProducts(products.filter((_, j) => j !== i));
    return (
        <div className="space-y-2">
            {products.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_80px_auto] gap-1 items-center">
                    <input
                        value={p.product_name || ''}
                        placeholder="Product"
                        onChange={e => update(i, { product_name: e.target.value })}
                        className="px-2 py-1 text-xs rounded border border-stone-200"
                    />
                    <input
                        type="number"
                        value={p.seats || 0}
                        onChange={e => update(i, { seats: Number(e.target.value) || 0 })}
                        className="px-2 py-1 text-xs rounded border border-stone-200"
                    />
                    <select
                        value={p.health || 'healthy'}
                        onChange={e => update(i, { health: e.target.value })}
                        className="px-1 py-1 text-xs rounded border border-stone-200"
                    >
                        <option value="healthy">healthy</option>
                        <option value="at-risk">at-risk</option>
                        <option value="churned">churned</option>
                    </select>
                    <button onClick={() => remove(i)} className="p-1 text-rose-500">
                        <X className="w-3 h-3" />
                    </button>
                </div>
            ))}
            <button onClick={add} className="flex gap-1 items-center text-xs font-semibold text-violet-600 hover:text-violet-700">
                <Plus className="w-3 h-3" /> Add product
            </button>
        </div>
    );
}

function NewsEditor({ news, setNews }) {
    const add = () => setNews([...news, { headline: '', url: '', source: '', published: '' }]);
    const update = (i, patch) => {
        const next = [...news];
        next[i] = { ...next[i], ...patch };
        setNews(next);
    };
    const remove = (i) => setNews(news.filter((_, j) => j !== i));
    return (
        <div className="space-y-2">
            {news.map((n, i) => (
                <div key={i} className="pb-2 space-y-1 border-b border-stone-100 last:border-0">
                    <div className="flex gap-1">
                        <input
                            value={n.headline || ''}
                            placeholder="Headline"
                            onChange={e => update(i, { headline: e.target.value })}
                            className="flex-1 px-2 py-1 text-xs rounded border border-stone-200"
                        />
                        <button onClick={() => remove(i)} className="p-1 text-rose-500">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                        <input
                            value={n.published || ''}
                            placeholder="YYYY-MM-DD"
                            onChange={e => update(i, { published: e.target.value })}
                            className="px-2 py-1 text-[11px] border border-stone-200 rounded"
                        />
                        <input
                            value={n.source || ''}
                            placeholder="Source"
                            onChange={e => update(i, { source: e.target.value })}
                            className="px-2 py-1 text-[11px] border border-stone-200 rounded"
                        />
                        <input
                            value={n.url || ''}
                            placeholder="URL"
                            onChange={e => update(i, { url: e.target.value })}
                            className="px-2 py-1 text-[11px] border border-stone-200 rounded"
                        />
                    </div>
                </div>
            ))}
            <button onClick={add} className="flex gap-1 items-center text-xs font-semibold text-violet-600 hover:text-violet-700">
                <Plus className="w-3 h-3" /> Add news item
            </button>
        </div>
    );
}

function LocationsSection({ account, onUpdate, getAuthHeader }) {
    const [locations, setLocations] = useState(account?.locations || []);
    const [adding, setAdding] = useState(false);
    const [newLoc, setNewLoc] = useState({ name: '', address: '', city: '', state: '', country: '', phone: '', is_hq: false });
    const [saving, setSaving] = useState(false);
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => { setLocations(account?.locations || []); }, [account]);

    const saveLocations = async (updated) => {
        setSaving(true);
        try {
            await axios.put(`${API}/accounts/${account.account_id}`, { locations: updated }, { headers: getAuthHeader() });
            setLocations(updated);
            onUpdate();
        } catch { toast.error('Failed to save locations'); }
        finally { setSaving(false); }
    };

    const addLocation = () => {
        if (!newLoc.city && !newLoc.name) return toast.error('Enter at least a city or office name');
        const loc = { ...newLoc, id: `loc_${Date.now()}` };
        const updated = [...locations, loc];
        saveLocations(updated);
        setNewLoc({ name: '', address: '', city: '', state: '', country: '', phone: '', is_hq: false });
        setAdding(false);
    };

    const removeLocation = (idx) => {
        const updated = locations.filter((_, i) => i !== idx);
        saveLocations(updated);
    };

    const setHQ = (idx) => {
        const updated = locations.map((l, i) => ({ ...l, is_hq: i === idx }));
        saveLocations(updated);
    };

    const INPUT = "w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20";

    return (
        <div className="pt-4 mt-6 border-t border-stone-100">
            <div className="flex justify-between items-center mb-3">
                <h3 className="flex gap-2 items-center text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <MapPinned className="w-4 h-4 text-stone-400" /> Office Locations
                    <span className="text-xs font-normal text-stone-400">({locations.length})</span>
                </h3>
                <button onClick={() => setAdding(!adding)}
                    className="flex gap-1 items-center text-xs font-medium text-stone-500 hover:text-stone-700">
                    <Plus className="w-3.5 h-3.5" /> Add Location
                </button>
            </div>

            {/* Existing locations */}
            {locations.length > 0 ? (
                <div className="space-y-2">
                    {locations.map((loc, i) => (
                        <div key={loc.id || i} className={`flex items-start gap-3 p-3 rounded-lg border ${loc.is_hq ? 'border-amber-200 bg-amber-50/50' : 'border-stone-200 bg-white'}`}>
                            <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${loc.is_hq ? 'text-amber-500' : 'text-stone-400'}`} />
                            <div className="flex-1 min-w-0">
                                <div className="flex gap-2 items-center">
                                    <span className="text-sm font-medium text-stone-800">{loc.name || loc.city || 'Office'}</span>
                                    {loc.is_hq && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">HQ</span>}
                                </div>
                                <p className="text-xs text-stone-500 mt-0.5">
                                    {[loc.address, loc.city, loc.state, loc.country].filter(Boolean).join(', ') || 'No address'}
                                </p>
                                {loc.phone && <p className="text-xs text-stone-400 mt-0.5">{loc.phone}</p>}
                            </div>
                            <div className="flex gap-1 items-center">
                                {!loc.is_hq && (
                                    <button onClick={() => setHQ(i)} title="Set as HQ"
                                        className="p-1 text-stone-400 hover:text-amber-500 text-[10px]">HQ</button>
                                )}
                                <button onClick={() => removeLocation(i)}
                                    className="p-1 text-stone-400 hover:text-red-500">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !adding && (
                <p className="mb-2 text-xs text-stone-400">No locations added yet.</p>
            )}

            {/* Add new location form */}
            {adding && (
                <div className="p-3 mt-3 space-y-2 rounded-lg border border-stone-200 bg-stone-50">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase mb-0.5">Office Name</label>
                            <input value={newLoc.name} onChange={e => setNewLoc(l => ({ ...l, name: e.target.value }))}
                                placeholder="e.g. New York HQ" className={INPUT} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase mb-0.5">Phone</label>
                            <input value={newLoc.phone} onChange={e => setNewLoc(l => ({ ...l, phone: e.target.value }))}
                                placeholder="+1-555-0100" className={INPUT} />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase mb-0.5">Address</label>
                            <input value={newLoc.address} onChange={e => setNewLoc(l => ({ ...l, address: e.target.value }))}
                                placeholder="123 Main St" className={INPUT} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase mb-0.5">City</label>
                            <input value={newLoc.city} onChange={e => setNewLoc(l => ({ ...l, city: e.target.value }))}
                                placeholder="New York" className={INPUT} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase mb-0.5">State</label>
                            <input value={newLoc.state} onChange={e => setNewLoc(l => ({ ...l, state: e.target.value }))}
                                placeholder="NY" className={INPUT} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold text-stone-500 uppercase mb-0.5">Country</label>
                            <input value={newLoc.country} onChange={e => setNewLoc(l => ({ ...l, country: e.target.value }))}
                                placeholder="US" className={INPUT} />
                        </div>
                        <label className="flex gap-2 items-center text-xs text-stone-600">
                            <input type="checkbox" checked={newLoc.is_hq} onChange={e => setNewLoc(l => ({ ...l, is_hq: e.target.checked }))}
                                className="rounded border-stone-300" />
                            Headquarters
                        </label>
                    </div>
                    <div className="flex gap-2 pt-1">
                        <button onClick={addLocation} disabled={saving}
                            className="px-3 py-1.5 text-xs bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                            {saving ? 'Saving...' : 'Add Location'}
                        </button>
                        <button onClick={() => setAdding(false)}
                            className="px-3 py-1.5 text-xs text-stone-500 hover:text-stone-700">Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AccountDetail() {
    const { accountId } = useParams();
    const { user, getAuthHeader } = useAuth();
    const { checkModule } = usePlan();
    const navigate = useNavigate();
    const hasATS = checkModule('ats');
    const isAdmin = user?.is_admin || user?.is_super_admin;
    const [account, setAccount] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [deals, setDeals] = useState([]);
    const [activities, setActivities] = useState([]);
    const [orgNodes, setOrgNodes] = useState([]);
    const [atsJobs, setAtsJobs] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]); // Org users available for assignment
    const [ownerSaving, setOwnerSaving] = useState(false);
    const [teamSaving, setTeamSaving] = useState(false);
    const [showAddTeamMember, setShowAddTeamMember] = useState(false);
    const [atsLoading, setAtsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [enriching, setEnriching] = useState(false);
    const [showEnrichMenu, setShowEnrichMenu] = useState(false);
    const [ldCandidate, setLdCandidate] = useState(null);
    const [ldSaving, setLdSaving] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [showAddContact, setShowAddContact] = useState(false);
    const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', title: '', department: '', linkedin_url: '', seniority: '', account_id: '' });
    const [contactSaving, setContactSaving] = useState(false);

    useEffect(() => { loadAll(); }, [accountId]);

    // Load org team members once for Owner + Team dropdowns
    useEffect(() => {
        axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() })
            .then(res => setTeamMembers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTeamMembers([]));
    }, []);

    // Reassign Owner (admin-only). Server logs old → new into change_log.
    const handleSetOwner = async (newOwnerId) => {
        if (!isAdmin) return;
        if (newOwnerId === (account?.owner_id || '')) return;
        setOwnerSaving(true);
        try {
            const res = await axios.put(
                `${API}/accounts/${accountId}/assign`,
                { owner_id: newOwnerId || null },
                { headers: getAuthHeader() }
            );
            setAccount(res.data);
            toast.success(newOwnerId ? 'Owner reassigned' : 'Owner cleared');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reassign owner');
        } finally {
            setOwnerSaving(false);
        }
    };

    const handleAddTeamMember = async (uid) => {
        if (!isAdmin || !uid) return;
        setTeamSaving(true);
        try {
            const res = await axios.put(
                `${API}/accounts/${accountId}/assign`,
                { add_member: uid },
                { headers: getAuthHeader() }
            );
            setAccount(res.data);
            setShowAddTeamMember(false);
            toast.success('Team member added');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add member');
        } finally {
            setTeamSaving(false);
        }
    };

    const handleRemoveTeamMember = async (uid) => {
        if (!isAdmin || !uid) return;
        setTeamSaving(true);
        try {
            const res = await axios.put(
                `${API}/accounts/${accountId}/assign`,
                { remove_member: uid },
                { headers: getAuthHeader() }
            );
            setAccount(res.data);
            toast.success('Team member removed');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove member');
        } finally {
            setTeamSaving(false);
        }
    };

    // Build a user_id → display info map for owner name + team rendering
    const userMap = Object.fromEntries(teamMembers.map(m => [m.user_id, m]));
    const ownerUser = account?.owner_id ? (userMap[account.owner_id] || null) : null;
    const teamUserRows = (account?.team_member_ids || [])
        .map(uid => userMap[uid] || { user_id: uid, name: '(unknown user)' });
    // Available candidates for team picker — exclude owner + already-on-team
    const teamCandidates = teamMembers.filter(m =>
        m.user_id !== account?.owner_id &&
        !(account?.team_member_ids || []).includes(m.user_id)
    );

    const loadAll = async () => {
        try {
            const [accRes, contactsRes, dealsRes, activitiesRes, orgRes] = await Promise.all([
                axios.get(`${API}/accounts/${accountId}`, { headers: getAuthHeader() }),
                axios.get(`${API}/contacts?account_id=${accountId}`, { headers: getAuthHeader() }),
                axios.get(`${API}/deals?account_id=${accountId}`, { headers: getAuthHeader() }),
                axios.get(`${API}/activities?account_id=${accountId}`, { headers: getAuthHeader() }),
                axios.get(`${API}/accounts/${accountId}/org-chart`, { headers: getAuthHeader() }),
            ]);
            setAccount(accRes.data);
            setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : (contactsRes.data?.data || []));
            setDeals(Array.isArray(dealsRes.data) ? dealsRes.data : (dealsRes.data?.data || []));
            setActivities(Array.isArray(activitiesRes.data) ? activitiesRes.data : (activitiesRes.data?.data || []));
            setOrgNodes(Array.isArray(orgRes.data) ? orgRes.data : (orgRes.data?.data || orgRes.data?.nodes || []));
            // Load ATS jobs if module enabled
            if (hasATS) {
                setAtsLoading(true);
                try {
                    const jobsRes = await axios.get(`${API}/ats/jobs?account_id=${accountId}&page_size=100`, { headers: getAuthHeader() });
                    setAtsJobs(Array.isArray(jobsRes.data) ? jobsRes.data : (jobsRes.data?.data || []));
                } catch { /* ATS not available — ignore */ }
                finally { setAtsLoading(false); }
            }
        } catch { toast.error('Failed to load account'); navigate('/accounts'); }
        finally { setLoading(false); }
    };

    const enrichFromWebsite = async () => {
        if (!account?.website && !account?.domain) return toast.error('No website URL on this company');
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const url = account.website || `https://${account.domain}`;
            const res = await axios.post(`${API}/enrichment/url`, { url }, { headers: getAuthHeader() });
            if (res.data) {
                await axios.put(`${API}/accounts/${accountId}`, { ...account, ...res.data }, { headers: getAuthHeader() });
                toast.success('Company enriched from website!');
                loadAll();
            } else {
                toast.warning('No enrichment data returned');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
        finally { setEnriching(false); }
    };

    const enrichLinkedInCompany = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/linkedin-company`,
                { account_id: accountId }, { headers: getAuthHeader() });
            if (res.data.enriched) {
                toast.success('LinkedIn company data enriched!');
                loadAll();
            } else {
                toast.warning(res.data.message || 'No data returned');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
        finally { setEnriching(false); }
    };

    const enrichExa = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/exa/company`,
                { account_id: account?.account_id }, { headers: getAuthHeader() });
            if (res.data.enriched) {
                toast.success('Exa enrichment complete!');
                loadAll();
            } else {
                toast.warning(res.data.message || 'No results found');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Exa enrichment failed'); }
        finally { setEnriching(false); }
    };

    // Find the Learning & Development officer at this company (AI research)
    // and offer to add them as a contact with key_role pre-set.
    const findLdContact = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/find-role-contact`,
                { account_id: accountId, role: 'ld_officer' },
                { headers: getAuthHeader(), timeout: 90000 });
            if (res.data?.found && res.data.candidate) {
                setLdCandidate(res.data.candidate);
            } else {
                toast.warning('No L&D contact could be identified for this company');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'L&D contact search failed'); }
        finally { setEnriching(false); }
    };

    const addLdContact = async () => {
        if (!ldCandidate) return;
        setLdSaving(true);
        try {
            await axios.post(`${API}/contacts`, {
                first_name: ldCandidate.first_name,
                last_name: ldCandidate.last_name,
                title: ldCandidate.title || undefined,
                email: ldCandidate.email || undefined,
                phone: ldCandidate.phone || undefined,
                linkedin_url: ldCandidate.linkedin_url || undefined,
                key_role: ldCandidate.key_role || 'ld_officer',
                notes: ldCandidate.notes || undefined,
                account_id: accountId,
            }, { headers: getAuthHeader() });
            toast.success(`${ldCandidate.first_name} ${ldCandidate.last_name} added as a contact`);
            setLdCandidate(null);
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create contact'); }
        finally { setLdSaving(false); }
    };

    const saveEdit = async () => {
        setEditSaving(true);
        try {
            await axios.put(`${API}/accounts/${accountId}`, editForm, { headers: getAuthHeader() });
            toast.success('Company updated');
            setShowEdit(false);
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update company'); }
        finally { setEditSaving(false); }
    };

    const saveContact = async () => {
        if (!contactForm.first_name || !contactForm.last_name) return toast.error('First and last name are required');
        setContactSaving(true);
        try {
            await axios.post(`${API}/contacts`, contactForm, { headers: getAuthHeader() });
            toast.success('Contact created');
            setShowAddContact(false);
            setContactForm({ first_name: '', last_name: '', email: '', phone: '', title: '', department: '', linkedin_url: '', seniority: '', account_id: '' });
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create contact'); }
        finally { setContactSaving(false); }
    };

    if (loading) return <div className="flex justify-center items-center h-64"><div className="w-8 h-8 rounded-full border-4 animate-spin border-stone-700 border-t-transparent" /></div>;
    if (!account) return null;

    const openDealsValue = deals.filter(d => !['Closed Won', 'Closed Lost'].includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0);
    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

    const tabs = [
        { key: 'overview', label: 'Overview', icon: Building2 },
        { key: 'data_insights', label: 'Data Insights', icon: Database },
        { key: 'contacts', label: `Contacts (${contacts.length})`, icon: Users },
        { key: 'deals', label: `Deals (${deals.length})`, icon: GitPullRequest },
        ...(hasATS ? [{ key: 'recruitment', label: `Recruitment (${atsJobs.length})`, icon: Briefcase }] : []),
        { key: 'org-chart', label: 'Org Chart', icon: Network },
        { key: 'activities', label: `Activities (${activities.length})`, icon: Activity },
        { key: 'changelog', label: `Change Log${account?.change_log?.length ? ` (${account.change_log.length})` : ''}`, icon: History },
        { key: 'ai_context', label: 'AI Context', icon: Sparkles },
        ...(account?.has_pitchbook_data
            ? [{ key: 'pitchbook', label: 'PitchBook', icon: Database }] : []),
    ];

    const ACCOUNT_FIELD_LABELS = {
        owner_id: 'Owner',
        team_members: 'Team Members',
    };

    const changeLog = [...(account?.change_log || [])].reverse();

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Back + Header */}
            <div className="flex justify-between items-start">
                <div className="flex gap-4 items-start">
                    <button onClick={() => navigate('/accounts')} className="p-2 mt-1 rounded-lg transition-colors hover:bg-stone-100 text-stone-400 hover:text-stone-600">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br rounded-xl border from-stone-100 to-stone-50 border-stone-200">
                                <span className="text-lg font-bold text-stone-700">{account.name?.charAt(0)}</span>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{account.name}</h1>
                                <div className="flex gap-3 items-center mt-1">
                                    {account.domain && <a href={`https://${account.domain}`} target="_blank" rel="noreferrer" className="flex gap-1 items-center text-xs text-stone-700 hover:text-stone-900"><Globe className="w-3 h-3" />{account.domain}</a>}
                                    {account.phone && <span className="flex gap-1 items-center text-xs text-stone-500"><Phone className="w-3 h-3" />{account.phone}</span>}
                                    {account.city && <span className="flex gap-1 items-center text-xs text-stone-500"><MapPin className="w-3 h-3" />{account.city}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 items-center">
                    <div className="relative">
                        <Button variant="outline" size="sm" onClick={() => setShowEnrichMenu(!showEnrichMenu)} disabled={enriching}>
                            {enriching ? <Loader2 className="mr-1 w-4 h-4 animate-spin" /> : <Sparkles className="mr-1 w-4 h-4" />}
                            Enrich
                        </Button>
                        {showEnrichMenu && (
                            <div className="absolute right-0 top-full z-10 py-1 mt-1 w-52 bg-white rounded-lg border shadow-lg border-stone-200">
                                <button onClick={enrichFromWebsite} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                    <Sparkles className="w-3.5 h-3.5 text-amber-500" /> AI Enrich from Website
                                </button>
                                <button onClick={enrichLinkedInCompany} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                    <Linkedin className="w-3.5 h-3.5 text-blue-600" /> LinkedIn Company
                                </button>
                                <button onClick={enrichExa} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                    <Search className="w-3.5 h-3.5 text-indigo-500" /> Exa Search
                                </button>
                                <button onClick={findLdContact} data-testid="find-ld-contact-btn"
                                    className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                    <GraduationCap className="w-3.5 h-3.5 text-violet-500" /> Find L&D Contact
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => navigate(`/proposals/new?account_id=${account.account_id}&account_name=${encodeURIComponent(account.name)}`)}
                        data-testid="create-proposal-btn"
                        className="flex gap-2 items-center px-3 py-2 text-sm rounded-lg border transition-colors border-stone-200 hover:bg-stone-50 text-stone-700">
                        <FileText className="w-4 h-4" /> New Proposal
                    </button>
                    <button onClick={() => navigate('/pipeline')} data-testid="add-deal-btn"
                        className="flex gap-2 items-center px-3 py-2 text-sm rounded-lg border transition-colors border-stone-200 hover:bg-stone-50 text-stone-700">
                        <Plus className="w-4 h-4" /> New Deal
                    </button>
                    <button onClick={() => { setEditForm({ name: account.name || '', website: account.website || '', phone: account.phone || '', city: account.city || '', country: account.country || '', industry: account.industry || '', size: account.size || '', status: account.status || '', description: account.description || '' }); setShowEdit(true); }} data-testid="edit-account-detail-btn" className="p-2 rounded-lg transition-colors hover:bg-stone-100 text-stone-500"><Edit3 className="w-4 h-4" /></button>
                </div>
            </div>

            {/* KPI Mini Cards */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Pipeline Value', value: formatVal(openDealsValue), color: 'text-stone-700', bg: 'bg-stone-100' },
                    { label: 'Total Contacts', value: contacts.length, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Open Deals', value: deals.filter(d => !['Closed Won', 'Closed Lost'].includes(d.stage)).length, color: 'text-violet-600', bg: 'bg-violet-50' },
                    { label: 'Health Score', value: account.health_score ? `${account.health_score}/100` : '—', color: account.health_score > 70 ? 'text-emerald-600' : 'text-amber-600', bg: account.health_score > 70 ? 'bg-emerald-50' : 'bg-amber-50' },
                ].map((m, i) => (
                    <div key={i} className="p-4 bg-white rounded-xl border border-stone-200">
                        <p className={`text-xl font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{m.label}</p>
                    </div>
                ))}
            </div>

            {/* ─── Owner & Team ──────────────────────────────────────────── */}
            <div className="p-4 bg-white rounded-xl border border-stone-200" data-testid="account-owner-team">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Owner */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-xs font-semibold tracking-wider uppercase text-stone-500">Owner</p>
                            {ownerSaving && <Loader2 className="w-3 h-3 animate-spin text-stone-400" />}
                        </div>
                        {isAdmin ? (
                            <select
                                data-testid="account-owner-select"
                                value={account.owner_id || ''}
                                disabled={ownerSaving}
                                onChange={e => handleSetOwner(e.target.value)}
                                className="px-3 py-2 w-full text-sm font-medium rounded-lg border cursor-pointer text-stone-800 bg-stone-50 border-stone-200 hover:border-stone-300 focus:border-stone-400 focus:outline-none">
                                <option value="">(unassigned)</option>
                                {teamMembers.map(m => (
                                    <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                                ))}
                            </select>
                        ) : (
                            <div className="flex gap-2 items-center px-3 py-2 rounded-lg bg-stone-50">
                                {ownerUser?.picture ? (
                                    <img src={ownerUser.picture} alt={ownerUser.name} className="w-6 h-6 rounded-full" />
                                ) : (
                                    <div className="flex justify-center items-center w-6 h-6 text-xs font-semibold rounded-full bg-stone-200 text-stone-600">
                                        {(ownerUser?.name || '?').charAt(0)}
                                    </div>
                                )}
                                <span className="text-sm font-medium text-stone-800">
                                    {ownerUser?.name || ownerUser?.email || '(unassigned)'}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Team Members */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-xs font-semibold tracking-wider uppercase text-stone-500">Team Members</p>
                            {isAdmin && (
                                <button
                                    data-testid="account-add-team-btn"
                                    onClick={() => setShowAddTeamMember(v => !v)}
                                    className="flex gap-1 items-center text-xs text-stone-500 hover:text-stone-800">
                                    <UserPlus className="w-3 h-3" /> Add
                                </button>
                            )}
                        </div>

                        {/* Add picker */}
                        {isAdmin && showAddTeamMember && (
                            <div className="p-2 mb-2 rounded-lg border bg-stone-50 border-stone-200">
                                {teamCandidates.length === 0 ? (
                                    <p className="px-1 py-1 text-xs text-stone-400">No available users to add.</p>
                                ) : (
                                    <select
                                        data-testid="account-add-team-select"
                                        autoFocus
                                        value=""
                                        disabled={teamSaving}
                                        onChange={e => e.target.value && handleAddTeamMember(e.target.value)}
                                        className="w-full text-sm bg-white border border-stone-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-stone-400">
                                        <option value="">Pick a user…</option>
                                        {teamCandidates.map(m => (
                                            <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        {teamUserRows.length === 0 ? (
                            <p className="px-3 py-2 text-sm rounded-lg text-stone-400 bg-stone-50">No team members</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {teamUserRows.map(m => (
                                    <div
                                        key={m.user_id}
                                        data-testid={`account-team-member-${m.user_id}`}
                                        className="flex gap-2 items-center py-1 pr-2 pl-1 rounded-full border bg-stone-50 border-stone-200 group">
                                        {m.picture ? (
                                            <img src={m.picture} alt={m.name} className="w-5 h-5 rounded-full" />
                                        ) : (
                                            <div className="w-5 h-5 rounded-full bg-stone-300 flex items-center justify-center text-[10px] font-semibold text-stone-700">
                                                {(m.name || '?').charAt(0)}
                                            </div>
                                        )}
                                        <span className="text-xs font-medium text-stone-700">{m.name || m.email || m.user_id}</span>
                                        {isAdmin && (
                                            <button
                                                data-testid={`account-remove-team-${m.user_id}`}
                                                onClick={() => handleRemoveTeamMember(m.user_id)}
                                                disabled={teamSaving}
                                                className="ml-1 opacity-0 transition-opacity text-stone-300 hover:text-red-500 group-hover:opacity-100">
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {!isAdmin && (
                    <p className="text-[10px] text-stone-400 mt-3">Only admins can reassign the owner or change team membership.</p>
                )}
            </div>

            {/* Tabs */}
            <div className="overflow-hidden bg-white rounded-xl border border-stone-200" data-testid="account-tabs">
                <div className="px-4 border-b border-stone-200">
                    <div className="flex gap-0">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-stone-700 text-stone-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}>
                                    <Icon className="w-4 h-4" />{tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-6">
                    {/* Overview */}
                    {activeTab === 'overview' && (
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Company Info</h3>
                                {[
                                    { label: 'Industry', value: Array.isArray(account.industry) ? account.industry.join(', ') : account.industry },
                                    { label: 'Company Size', value: account.size },
                                    { label: 'Employee Number Approx', value: account.num_employees || account.employee_range },
                                    // Prefer the imported range label ("$1M - $5M", "$500M+") when
                                    // present, fall back to formatted numeric. Matches the
                                    // num_employees / employee_range split for headcount.
                                    { label: 'Annual Revenue', value: account.annual_revenue_range || (account.annual_revenue ? formatVal(account.annual_revenue) : '—') },
                                    { label: 'Status', value: account.status },
                                    { label: 'Street Address', value: account.address },
                                    { label: 'City', value: account.city },
                                    { label: 'State / Province', value: account.state },
                                    { label: 'Country', value: account.country },
                                    { label: 'Region', value: account.region },
                                    { label: 'Company HQ Country', value: account.hq_country },
                                    { label: 'Website URL', value: account.website, isLink: true },
                                    { label: 'Company LinkedIn URL', value: account.linkedin_url, isLink: true },
                                    { label: 'Company Phone', value: account.phone },
                                    { label: 'Company Email', value: account.email },
                                    { label: 'Lead Investor', value: account.lead_investor },
                                    { label: 'Investor Type', value: account.investor_type },
                                ].map(f => (
                                    <div key={f.label} className="flex gap-4">
                                        <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider w-40 flex-shrink-0 mt-0.5">{f.label}</span>
                                        {f.isLink && f.value ? (
                                            <a href={f.value.startsWith('http') ? f.value : `https://${f.value}`} target="_blank" rel="noreferrer"
                                                className="text-sm text-blue-600 break-all hover:underline">{f.value}</a>
                                        ) : (
                                            <span className="text-sm break-words text-stone-700">{f.value || '—'}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div>
                                {account.description && (
                                    <div>
                                        <h3 className="mb-2 text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Description</h3>
                                        <div className="max-h-[400px] overflow-y-auto">
                                            {account.description.split('\n\n').map((para, i) => (
                                                <p key={i} className="mb-2 text-sm leading-relaxed text-stone-600 last:mb-0">{para}</p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mt-4">
                                    <h3 className="mb-2 text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tags</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {(account.tags || []).map(tag => (
                                            <span key={tag} className="px-2 py-1 text-xs rounded-full border bg-stone-100 text-stone-700 border-stone-200">{tag}</span>
                                        ))}
                                        {(!account.tags || account.tags.length === 0) && <span className="text-xs text-stone-400">No tags</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Locations */}
                    {activeTab === 'overview' && (
                        <LocationsSection account={account} onUpdate={loadAll} getAuthHeader={getAuthHeader} />
                    )}

                    {/* Prep-brief research: products, priorities, news */}
                    {activeTab === 'overview' && (
                        <AccountResearchSection account={account} onUpdate={loadAll} getAuthHeader={getAuthHeader} />
                    )}

                    {/* LinkedIn Company Data */}
                    {activeTab === 'overview' && account.linkedin_data && (
                        <LinkedInCompanySection data={account.linkedin_data} />
                    )}

                    {/* Data Insights */}
                    {activeTab === 'data_insights' && (
                        <AccountDataInsights account={account} />
                    )}

                    {/* Contacts */}
                    {activeTab === 'contacts' && (
                        <div className="space-y-3">
                            <div className="flex justify-between items-center mb-4">
                                <p className="text-sm text-stone-500">{contacts.length} contacts at {account.name}</p>
                                <div className="flex gap-2 items-center">
                                    {contacts.length > 0 && (
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => {
                                                setSelectedContacts(contacts);
                                                setShowEnrollModal(true);
                                            }}
                                            data-testid="bulk-enroll-all-btn"
                                            className="h-8"
                                        >
                                            <Workflow className="w-3.5 h-3.5 mr-1.5" />
                                            Enroll All in Campaign
                                        </Button>
                                    )}
                                    <button onClick={() => { setContactForm(f => ({ ...f, account_id: account?.account_id })); setShowAddContact(true); }} className="flex gap-1 items-center text-sm text-stone-700">
                                        <Plus className="w-4 h-4" /> Add Contact
                                    </button>
                                </div>
                            </div>
                            {contacts.map(c => (
                                <div key={c.contact_id} className="flex gap-4 items-center p-3 rounded-lg border transition-colors cursor-pointer hover:bg-stone-50 border-stone-100 group">
                                    <div className="overflow-hidden flex-shrink-0 w-9 h-9 rounded-full bg-stone-100" onClick={() => navigate(`/contacts/${c.contact_id}`)}>
                                        {c.avatar_url ? <img src={c.avatar_url} alt={`${c.first_name} ${c.last_name}`} className="object-cover w-full h-full" /> : <div className="flex justify-center items-center w-full h-full text-xs font-semibold text-stone-700">{c.first_name?.charAt(0)}</div>}
                                    </div>
                                    <div className="flex-1 min-w-0" onClick={() => navigate(`/contacts/${c.contact_id}`)}>
                                        <p className="text-sm font-medium text-stone-900">{c.first_name} {c.last_name}</p>
                                        <p className="text-xs text-stone-400">{c.title}{c.department ? ` · ${c.department}` : ''}</p>
                                    </div>
                                    {c.disc_profile && <DISCBadge profile={c.disc_profile} secondary={c.disc_secondary} size="sm" />}
                                    {c.role_in_deal && <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[c.role_in_deal] || 'bg-stone-100 text-stone-600'}`}>{c.role_in_deal}</span>}
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedContacts([c]);
                                            setShowEnrollModal(true);
                                        }}
                                        className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 opacity-0 group-hover:opacity-100 transition-all"
                                        title="Enroll in campaign"
                                        data-testid={`enroll-contact-${c.contact_id}`}
                                    >
                                        <Workflow className="w-4 h-4" />
                                    </button>
                                    {c.email && <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700"><Mail className="w-4 h-4" /></a>}
                                    <ChevronRight className="w-4 h-4 text-stone-300" onClick={() => navigate(`/contacts/${c.contact_id}`)} />
                                </div>
                            ))}
                            {contacts.length === 0 && <p className="py-6 text-sm text-center text-stone-400">No contacts yet</p>}
                        </div>
                    )}

                    {/* Deals */}
                    {activeTab === 'deals' && (
                        <div className="space-y-3">
                            {deals.map(deal => (
                                <div key={deal.deal_id} className="flex gap-4 items-center p-4 rounded-lg border transition-all cursor-pointer border-stone-200 hover:border-stone-300 hover:bg-stone-100/20" onClick={() => navigate(`/deals/${deal.deal_id}`)}>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-stone-900">{deal.title}</p>
                                        <div className="flex gap-3 items-center mt-1">
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${STAGE_COLORS[deal.stage] || 'bg-stone-50 text-stone-600'}`}>{deal.stage}</span>
                                            {deal.expected_close_date && <span className="text-xs text-stone-400">Close: {deal.expected_close_date}</span>}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-bold text-stone-900">{deal.value ? formatVal(deal.value) : '—'}</p>
                                        {deal.probability !== undefined && <p className="text-xs text-stone-400">{deal.probability}% probability</p>}
                                    </div>
                                    {deal.ai_risk_score !== undefined && (
                                        <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${deal.ai_risk_score > 60 ? 'risk-high' : deal.ai_risk_score > 30 ? 'risk-medium' : 'risk-low'}`}>
                                            Risk: {deal.ai_risk_score}
                                        </div>
                                    )}
                                    <ChevronRight className="w-4 h-4 text-stone-300" />
                                </div>
                            ))}
                            {deals.length === 0 && <p className="py-6 text-sm text-center text-stone-400">No deals yet</p>}
                        </div>
                    )}

                    {/* Recruitment */}
                    {activeTab === 'recruitment' && hasATS && (
                        <div className="space-y-4">
                            {/* Recruitment Stats */}
                            {atsJobs.length > 0 && (
                                <div className="grid grid-cols-4 gap-3 mb-4">
                                    {[
                                        { label: 'Total Jobs', value: atsJobs.length, color: 'text-blue-600', bg: 'bg-blue-50', icon: Briefcase },
                                        { label: 'Active', value: atsJobs.filter(j => j.status === 'active').length, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: Eye },
                                        { label: 'Open Slots', value: atsJobs.reduce((s, j) => s + Math.max(0, (j.slots || 1) - (j.filled_slots || 0)), 0), color: 'text-amber-600', bg: 'bg-amber-50', icon: Users },
                                        { label: 'Hired', value: atsJobs.reduce((s, j) => s + (j.filled_slots || 0), 0), color: 'text-violet-600', bg: 'bg-violet-50', icon: UserCheck },
                                    ].map((stat, i) => {
                                        const Icon = stat.icon;
                                        return (
                                            <div key={i} className={`${stat.bg} rounded-lg p-3 flex items-center gap-3`}>
                                                <div className={`flex justify-center items-center w-8 h-8 rounded-lg bg-white/60`}>
                                                    <Icon className={`w-4 h-4 ${stat.color}`} />
                                                </div>
                                                <div>
                                                    <p className={`text-lg font-bold ${stat.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{stat.value}</p>
                                                    <p className="text-xs text-stone-500">{stat.label}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Job Orders List */}
                            {atsLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                                </div>
                            ) : atsJobs.length > 0 ? (
                                <div className="space-y-3">
                                    {atsJobs.map(job => {
                                        const JOB_STATUS_COLORS = {
                                            active: 'bg-emerald-50 text-emerald-700',
                                            draft: 'bg-stone-100 text-stone-600',
                                            on_hold: 'bg-amber-50 text-amber-700',
                                            completed: 'bg-blue-50 text-blue-700',
                                            cancelled: 'bg-red-50 text-red-700',
                                            for_review: 'bg-violet-50 text-violet-700',
                                        };
                                        const totalApplicants = job.stats?.total_applicants || 0;
                                        const filledSlots = job.filled_slots || 0;
                                        const totalSlots = job.slots || 1;
                                        const fillPct = Math.min(100, Math.round((filledSlots / totalSlots) * 100));

                                        return (
                                            <div key={job.job_id}
                                                onClick={() => navigate(`/recruitment/jobs/${job.job_id}`)}
                                                className="flex gap-4 items-center p-4 rounded-lg border transition-all cursor-pointer border-stone-200 hover:border-stone-300 hover:bg-stone-50/50 group">
                                                <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-blue-50 rounded-lg">
                                                    <Briefcase className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex gap-2 items-center">
                                                        <p className="text-sm font-medium truncate text-stone-900">{job.title}</p>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${JOB_STATUS_COLORS[job.status] || 'bg-stone-100 text-stone-600'}`}>
                                                            {(job.status || 'draft').replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-1.5">
                                                        <span className="flex gap-1 items-center text-xs text-stone-500">
                                                            <Users className="w-3 h-3" /> {totalApplicants} applicant{totalApplicants !== 1 ? 's' : ''}
                                                        </span>
                                                        <span className="flex gap-1 items-center text-xs text-stone-500">
                                                            <UserCheck className="w-3 h-3" /> {filledSlots}/{totalSlots} filled
                                                        </span>
                                                        {job.owner_name && (
                                                            <span className="text-xs text-stone-400">
                                                                Owner: {job.owner_name}
                                                            </span>
                                                        )}
                                                        {job.created_at && (
                                                            <span className="flex gap-1 items-center text-xs text-stone-400">
                                                                <Clock className="w-3 h-3" /> {new Date(job.created_at).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Fill Progress */}
                                                <div className="flex-shrink-0 w-24">
                                                    <div className="flex justify-between items-center mb-1 text-xs">
                                                        <span className="text-stone-500">{fillPct}%</span>
                                                    </div>
                                                    <div className="w-full h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${fillPct >= 100 ? 'bg-emerald-500' : fillPct > 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                                                            style={{ width: `${fillPct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-4 h-4 transition-colors text-stone-300 group-hover:text-stone-500" />
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="py-10 text-center">
                                    <Briefcase className="mx-auto mb-3 w-10 h-10 text-stone-300" />
                                    <p className="mb-1 text-sm text-stone-500">No recruitment job orders for this company</p>
                                    <p className="mb-4 text-xs text-stone-400">Create a job order linked to this account from the Recruitment module</p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/recruitment')}>
                                        <Briefcase className="mr-1 w-4 h-4" /> Go to Recruitment
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Org Chart */}
                    {activeTab === 'org-chart' && (
                        <div>
                            <div data-testid="org-chart-container">
                                <OrgChart
                                    orgNodes={orgNodes}
                                    accountId={accountId}
                                    onNodeClick={(nodeData) => {
                                        if (nodeData.contact_id) {
                                            navigate(`/contacts/${nodeData.contact_id}`);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Activities */}
                    {activeTab === 'activities' && (
                        <div className="space-y-3">
                            {activities.slice(0, 15).map(a => (
                                <div key={a.activity_id} className="flex gap-3 items-start p-3 rounded-lg border transition-colors hover:bg-stone-50 border-stone-100">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${a.type === 'email' ? 'bg-blue-100' : a.type === 'call' ? 'bg-green-100' : a.type === 'meeting' ? 'bg-amber-100' : 'bg-stone-100'}`}>
                                        {a.type === 'email' ? '📧' : a.type === 'call' ? '📞' : a.type === 'meeting' ? '📅' : '📝'}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-stone-900">{a.title}</p>
                                        <p className="text-xs text-stone-400">{a.description || a.type}</p>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{a.status}</span>
                                </div>
                            ))}
                            {activities.length === 0 && <p className="py-6 text-sm text-center text-stone-400">No activities yet</p>}
                        </div>
                    )}

                    {/* Change Log */}
                    {activeTab === 'changelog' && (
                        <div data-testid="account-changelog">
                            {changeLog.length === 0 ? (
                                <p className="py-8 text-sm text-center text-stone-400">No changes recorded yet. Owner and team assignments will appear here.</p>
                            ) : (
                                <div className="space-y-3">
                                    {changeLog.map((entry, idx) => (
                                        <div key={entry.log_id || idx} data-testid={`account-changelog-entry-${idx}`}
                                             className="flex gap-3 p-3 rounded-lg border transition-colors border-stone-100 hover:bg-stone-50">
                                            <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 rounded-full bg-stone-100">
                                                <History className="w-4 h-4 text-stone-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap gap-2 items-center">
                                                    <span className="text-sm font-semibold text-stone-800">{entry.user_name || 'Unknown'}</span>
                                                    <span className="text-xs text-stone-400">{formatDate(entry.timestamp)}</span>
                                                </div>
                                                <div className="mt-1 space-y-1">
                                                    {(entry.changes || []).map((c, ci) => (
                                                        <div key={ci} className="text-xs text-stone-600">
                                                            <span className="font-medium text-stone-700">{ACCOUNT_FIELD_LABELS[c.field] || c.field}</span>
                                                            {': '}
                                                            <span className="line-through text-stone-400">{c.old_value || '(empty)'}</span>
                                                            <span className="mx-1 text-stone-300">→</span>
                                                            <span className="font-medium text-stone-800">{c.new_value || '(empty)'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* AI Context */}
                    {activeTab === 'ai_context' && (
                        <MdFilePanel type="account" entityId={accountId} />
                    )}

                    {/* PitchBook data */}
                    {activeTab === 'pitchbook' && (
                        <PitchBookRecordTab crmType="account" crmId={accountId} />
                    )}
                </div>
            </div>

            {/* L&D contact candidate */}
            <Dialog open={!!ldCandidate} onOpenChange={(o) => { if (!o) setLdCandidate(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex gap-2 items-center">
                            <GraduationCap className="w-5 h-5 text-violet-500" /> L&D Contact Found
                        </DialogTitle>
                    </DialogHeader>
                    {ldCandidate && (
                        <div className="mt-2 space-y-3">
                            <div className="p-4 bg-violet-50 rounded-xl border border-violet-100">
                                <p className="text-sm font-semibold text-stone-900">
                                    {ldCandidate.first_name} {ldCandidate.last_name}
                                </p>
                                <p className="text-xs text-stone-600 mt-0.5">{ldCandidate.title || 'Title unknown'}</p>
                                <div className="mt-2 space-y-1">
                                    {ldCandidate.email && <p className="text-xs text-stone-600 flex items-center gap-1.5"><Mail className="w-3 h-3" />{ldCandidate.email}</p>}
                                    {ldCandidate.phone && <p className="text-xs text-stone-600 flex items-center gap-1.5"><Phone className="w-3 h-3" />{ldCandidate.phone}</p>}
                                    {ldCandidate.linkedin_url && (
                                        <a href={ldCandidate.linkedin_url} target="_blank" rel="noreferrer"
                                            className="text-xs text-blue-600 hover:underline flex items-center gap-1.5">
                                            <Linkedin className="w-3 h-3" />LinkedIn profile
                                        </a>
                                    )}
                                </div>
                                {ldCandidate.notes && <p className="text-[11px] text-stone-500 mt-2 italic">{ldCandidate.notes}</p>}
                            </div>
                            <p className="text-[11px] text-stone-400">
                                AI-researched result — verify before outreach. Adding creates a contact on this company with the L&D key role set.
                            </p>
                            <div className="flex gap-2 justify-end">
                                <Button variant="outline" size="sm" onClick={() => setLdCandidate(null)}>Dismiss</Button>
                                <Button size="sm" onClick={addLdContact} disabled={ldSaving} data-testid="add-ld-contact-btn">
                                    {ldSaving ? <Loader2 className="mr-1 w-4 h-4 animate-spin" /> : <UserPlus className="mr-1 w-4 h-4" />}
                                    Add as Contact
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Company Dialog */}
            <Dialog open={showEdit} onOpenChange={setShowEdit}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Company</DialogTitle>
                    </DialogHeader>
                    <div className="mt-2 space-y-3">
                        {[
                            { key: 'name', label: 'Company Name', required: true },
                            { key: 'website', label: 'Website URL' },
                            { key: 'linkedin_url', label: 'Company LinkedIn URL' },
                            { key: 'phone', label: 'Company Phone' },
                            { key: 'email', label: 'Company Email' },
                            { key: 'address', label: 'Street Address' },
                            { key: 'city', label: 'City' },
                            { key: 'state', label: 'State / Province' },
                            { key: 'country', label: 'Country' },
                            { key: 'region', label: 'Region' },
                            { key: 'hq_country', label: 'Company HQ Country' },
                            { key: 'industry', label: 'Industry' },
                            { key: 'size', label: 'Company Size' },
                            { key: 'num_employees', label: 'Employee Number Approx', type: 'number' },
                            { key: 'lead_investor', label: 'Lead Investor' },
                        ].map(f => (
                            <div key={f.key}>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">{f.label}{f.required && ' *'}</label>
                                <input type={f.type || 'text'} value={editForm[f.key] || ''} onChange={e => setEditForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                        ))}
                        {/* Investor Type dropdown — canonical 5-option list */}
                        <div>
                            <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Investor Type</label>
                            <select value={editForm.investor_type || ''} onChange={e => setEditForm(prev => ({ ...prev, investor_type: e.target.value }))}
                                className="px-3 py-2 w-full text-sm bg-white rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="">Select...</option>
                                {['Angel', 'Venture Capital', 'Private Equity', 'Family Office', 'Institutional'].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Status</label>
                            <select value={editForm.status || ''} onChange={e => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                                className="px-3 py-2 w-full text-sm bg-white rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="">Select...</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Prospect">Prospect</option>
                                <option value="Customer">Customer</option>
                                <option value="Churned">Churned</option>
                            </select>
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Description</label>
                            <textarea value={editForm.description || ''} onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                rows={4} className="px-3 py-2 w-full text-sm rounded-lg border resize-none border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button variant="outline" size="sm" onClick={() => setShowEdit(false)}>Cancel</Button>
                            <Button size="sm" onClick={saveEdit} disabled={editSaving || !editForm.name}>
                                {editSaving ? <Loader2 className="mr-1 w-4 h-4 animate-spin" /> : null}
                                {editSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Contact Dialog */}
            <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Contact to {account?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="mt-2 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">First Name *</label>
                                <input value={contactForm.first_name} onChange={e => setContactForm(prev => ({ ...prev, first_name: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                            <div>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Last Name *</label>
                                <input value={contactForm.last_name} onChange={e => setContactForm(prev => ({ ...prev, last_name: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Email</label>
                            <input type="email" value={contactForm.email} onChange={e => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                                className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Phone</label>
                                <input value={contactForm.phone} onChange={e => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                            <div>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Job Title</label>
                                <input value={contactForm.title} onChange={e => setContactForm(prev => ({ ...prev, title: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Department</label>
                                <input value={contactForm.department} onChange={e => setContactForm(prev => ({ ...prev, department: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                            <div>
                                <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Seniority</label>
                                <select value={contactForm.seniority} onChange={e => setContactForm(prev => ({ ...prev, seniority: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm bg-white rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                    <option value="">Select...</option>
                                    <option value="C-Level">C-Level</option>
                                    <option value="VP">VP</option>
                                    <option value="Director">Director</option>
                                    <option value="Manager">Manager</option>
                                    <option value="Senior">Senior</option>
                                    <option value="Mid-Level">Mid-Level</option>
                                    <option value="Junior">Junior</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">LinkedIn URL</label>
                            <input value={contactForm.linkedin_url} onChange={e => setContactForm(prev => ({ ...prev, linkedin_url: e.target.value }))}
                                placeholder="https://linkedin.com/in/..."
                                className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button variant="outline" size="sm" onClick={() => setShowAddContact(false)}>Cancel</Button>
                            <Button size="sm" onClick={saveContact} disabled={contactSaving || !contactForm.first_name || !contactForm.last_name}>
                                {contactSaving ? <Loader2 className="mr-1 w-4 h-4 animate-spin" /> : null}
                                {contactSaving ? 'Creating...' : 'Create Contact'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Enroll in Campaign Modal */}
            <EnrollCampaignModal
                isOpen={showEnrollModal}
                onClose={() => {
                    setShowEnrollModal(false);
                    setSelectedContacts([]);
                }}
                contacts={selectedContacts}
                onSuccess={() => {
                    setShowEnrollModal(false);
                    setSelectedContacts([]);
                }}
            />
        </div>
    );
}
